import axios from 'axios';

import getAuthSession from './auth';
import { CompanyCertification, CompanyCertificationSubmitData } from '../interfaces/users.interface';
import { GridSortModel } from '@mui/x-data-grid/models/gridSortModel';
import { GridFilterModel } from '@mui/x-data-grid/models/gridFilterModel';

export default async function createCompanyCertification(companyCertificationData: CompanyCertificationSubmitData) {
  const authSession = await getAuthSession();
  const preResponse = await axios.post(
    `${process.env.REACT_APP_BASE_API}/company-certifications/`,
    { ...companyCertificationData },
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  const newCompanyCert = { ...preResponse.data, certification: preResponse.data.certification.id, applicationVersions: companyCertificationData.applicationVersions, sites: companyCertificationData.sites };
  delete newCompanyCert.document;
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/company-certifications/${preResponse.data.id}/`,
    newCompanyCert,
    {
      headers: {
        'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
    },
  );
  return response.data as CompanyCertification;
}

export async function deleteCompanyCertification(companyCertification: CompanyCertification) {
  const authSession = await getAuthSession();
  const response = await axios.delete(
    `${process.env.REACT_APP_BASE_API}/company-certifications/${companyCertification.id}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function updateCompanyCertificationApproval(companyCertificationId: number, approved: boolean) {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/company-certifications/${companyCertificationId}/`,
    {
      adminApproved: approved,
    },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function updateCompanyCertificatioReviewed(companyCertificationId: number, reviewed: boolean) {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/company-certifications/${companyCertificationId}/`,
    {
      adminReviewed: reviewed,
    },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function getCompanyCertifications(companyId: number) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/company-certifications/?company=${companyId}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data.results;
}

export async function getAllCompanyCertifications() {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/company-certifications/?limit=99999`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data.results;
}

export type GetDataGridParams = {
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
};

export async function getCompanyCertificationsForDataGrid({
  page,
  pageSize,
  sortModel,
  filterModel,
}: GetDataGridParams): Promise<{ results: CompanyCertification[]; count: number }> {
  const authSession = await getAuthSession();

  const params: Record<string, any> = {
    limit: pageSize,
    offset: page * pageSize,
  };

  // --- Sorting ---
  const sort = sortModel?.[0];
  if (sort?.field && sort?.sort) {
    params.ordering = sort.sort === 'asc' ? sort.field : `-${sort.field}`;
  }

  // --- Filtering ---
  if (filterModel?.items?.length) {
    filterModel.items.forEach((item) => {
      if (item.value === undefined || item.value === null || String(item.value).trim() === '') return;
      if (!item.columnField) return;

      params[item.columnField] = item.value;
    });
  }

  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/company-certifications/`,
    {
      headers: {
        Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
      },
      params,
    },
  );

  return {
    results: response.data.results as CompanyCertification[],
    count: response.data.count,
  };
}
